import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { DateTime } from 'luxon'
import type { Section } from '@breakoutlearning/firebase-repository/models/Section'
import { ClockIcon } from 'components/icons/Clock'
import { useTranslationTyped } from 'i18n/i18n'

// At or below this many days remaining the badge switches to its urgent
// (eggshell / accent) colouring. Matches the Figma "3 days out or less" state.
const URGENT_DAYS_THRESHOLD = 3

/**
 * A persistent badge showing when a section's free access window ends (BL-4598).
 * Rendered on the instructor section, instructor assignment, and student
 * assignment pages. It is only visible while the window is active and
 * automatically disappears once the period has passed. As the deadline nears
 * (<= 3 days) the badge switches to an accent colour to draw attention.
 */
export const FreeAccessBadge = observer(function FreeAccessBadge({
  section,
  className,
}: {
  section: Section
  className?: string
}) {
  const { tt } = useTranslationTyped()

  const daysRemaining = section.freeAccessDaysRemaining
  const freeAccessUntil = section.data.freeAccessUntil

  // Hidden outside the free access period (daysRemaining is null once expired).
  if (daysRemaining === null || !freeAccessUntil) return null

  const isUrgent = daysRemaining <= URGENT_DAYS_THRESHOLD
  const formattedDate =
    DateTime.fromJSDate(freeAccessUntil).toFormat('LLL d, h:mm a')

  return (
    <div
      data-testid="free-access-badge"
      role="status"
      aria-label={tt.free_access_badge.aria_label({ date: formattedDate })}
      className={classNames(
        'inline-flex shrink-0 items-center justify-center gap-1 rounded-2xl border px-5 py-2.5',
        isUrgent
          ? 'border-fixed-accent-color bg-fixed-eggshell'
          : 'border-outline-medium bg-surface-bright',
        className
      )}
    >
      <ClockIcon
        size={15}
        className={classNames(
          'shrink-0',
          isUrgent ? 'stroke-fixed-accent-color' : 'stroke-on-surface-var'
        )}
      />
      <div className="flex flex-col items-start leading-tight">
        <div className="text-label-medium text-on-surface-var">
          {tt.free_access_badge.label()}
        </div>
        <div className="text-label-large text-on-surface">{formattedDate}</div>
      </div>
    </div>
  )
})
